/**
 * Currency formatter function
 *
 * @param {Number} value - Value to be formatted
 * @param {String} locale
 * @param {String} currency - the unit the server named for this money. XAF is
 *   the fallback for money whose unit nothing has told us, NOT a fact about it.
 * @returns {String} - Formatted value
 */

// Used only where no currency is available at all. Named rather than inlined
// as a default parameter: a default silently wins, and an assumed unit on a
// money path is expensive the first time it is wrong (D-HARM-9).
export const FALLBACK_CURRENCY = "XAF";

const formatCurrency = (
  value: number | string,
  locale = "fr-CM",
  currency?: string
) => {
  // An empty string reaches here whenever the session has no currency yet, and
  // Intl throws on one rather than falling back.
  const unit = currency?.trim() || FALLBACK_CURRENCY;
  const numeric = Number(value);

  // XAF carries zero minor units, so Intl ROUNDS a fractional amount to whole
  // francs: 12345.67 renders as 12 346 while 12345.67 is charged. That is the
  // same defect as the parseInt this replaced -- shown is not sent -- so where
  // a value actually has a fraction it is rendered in full rather than
  // rounded away. A whole amount keeps the currency's own convention, so every
  // ordinary render is unchanged.
  const fractional = Number.isFinite(numeric) && numeric % 1 !== 0;

  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: unit,
    ...(fractional
      ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
      : {}),
  });

  return formatter.format(numeric);
};

export default formatCurrency;
